import { useState, useRef } from "react";
import html2canvas from "html2canvas";
import logo from "../assets/logo.jpg";

/* =========================
   QUIZ QUESTIONS
========================= */
const questions = [
  {
    question: "Which activity excites you the most?",
    options: [
      { text: "Solving complex problems", type: "Innovator" },
      { text: "Leading teams or projects", type: "Leader" },
      { text: "Building or designing things", type: "Builder" },
      { text: "Helping communities grow", type: "Advocate" },
    ],
  },
  {
    question: "What motivates you the most?",
    options: [
      { text: "Creating new ideas", type: "Innovator" },
      { text: "Influencing people positively", type: "Leader" },
      { text: "Developing practical solutions", type: "Builder" },
      { text: "Improving society", type: "Advocate" },
    ],
  },
  {
    question: "Which environment do you enjoy most?",
    options: [
      { text: "Innovation labs", type: "Innovator" },
      { text: "Leadership roles", type: "Leader" },
      { text: "Workshops or tech spaces", type: "Builder" },
      { text: "Community projects", type: "Advocate" },
    ],
  },
  {
    question: "What role do you usually take in a group?",
    options: [
      { text: "Idea generator", type: "Innovator" },
      { text: "Team leader", type: "Leader" },
      { text: "Problem fixer", type: "Builder" },
      { text: "Supporter and motivator", type: "Advocate" },
    ],
  },
  {
    question: "What impact would you most like to create?",
    options: [
      { text: "Innovative technologies", type: "Innovator" },
      { text: "Strong leadership", type: "Leader" },
      { text: "Practical solutions", type: "Builder" },
      { text: "Stronger communities", type: "Advocate" },
    ],
  },
];

/* =========================
   RESULT DESCRIPTIONS
========================= */
const resultDescriptions = {
  Innovator:
    "You thrive on creativity and new ideas. You are driven to build the future through innovation.",
  Leader:
    "You inspire people and guide teams toward meaningful change and impact.",
  Builder:
    "You enjoy creating practical solutions and turning ideas into reality.",
  Advocate:
    "You are passionate about people, fairness, and building stronger communities.",
};

export default function QuizPage() {

  /* =========================
     STATE MANAGEMENT
  ========================= */

  // Track which question user is on
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Store user answers
  const [answers, setAnswers] = useState([]);

  // Store final result
  const [result, setResult] = useState(null);

  // Reference for the result card (used for image download)
  const resultRef = useRef();

  /* =========================
     PROGRESS BAR CALCULATION
  ========================= */

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  /* =========================
     HANDLE ANSWER CLICK
  ========================= */

  const handleAnswer = (type) => {

    // Add answer to array
    const newAnswers = [...answers, type];
    setAnswers(newAnswers);

    // Move to next question if quiz not finished
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult(newAnswers);
    }
  };

  /* =========================
     CALCULATE QUIZ RESULT
  ========================= */

  const calculateResult = (answers) => {

    const counts = {};

    // Count how many times each result appears
    answers.forEach((type) => {
      counts[type] = (counts[type] || 0) + 1;
    });

    // Find the highest count
    const finalResult = Object.keys(counts).reduce((a, b) =>
      counts[a] > counts[b] ? a : b
    );

    setResult(finalResult);
  };

  /* =========================
     DOWNLOAD RESULT CARD
  ========================= */

  const downloadCard = () => {
    html2canvas(resultRef.current).then((canvas) => {

      const link = document.createElement("a");
      link.download = "ogth-talent-result.png";
      link.href = canvas.toDataURL();
      link.click();

    });
  };

  /* =========================
     RETAKE QUIZ
  ========================= */

  const retakeQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
  };

  /* =========================
     SHARE CONTENT
  ========================= */

  const quizUrl = "https://ogth.org/discover-your-talent";

  const shareText =
    `I discovered my talent type with the OGTH Talent Quiz! Take the quiz: ${quizUrl}`;

  return (
    <section className="bg-gray-50 flex items-center justify-center p-auto md:p-16 lg:p-16">

      <div className="max-w-xl w-full bg-white p-10 rounded-xl shadow-lg">

        {/* =========================
           QUIZ QUESTIONS SECTION
        ========================= */}

        {!result && (
          <>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 h-3 rounded-full mb-6">
              <div
                className="bg-brand h-3 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              ></div>
            </div>

            {/* Question Number Indicator */}
            <p className="text-sm text-gray-500 mb-2 text-center">
              Question {currentQuestion + 1} / {questions.length}
            </p>

            {/* Question Text */}
            <h2 className="text-2xl font-bold mb-6 text-center">
              {questions[currentQuestion].question}
            </h2>

            {/* Answer Options */}
            <div className="space-y-4">

              {questions[currentQuestion].options.map((option, index) => (

                <button
                  key={index}
                  onClick={() => handleAnswer(option.type)}
                  className="w-full bg-white border p-4 rounded-lg hover:bg-brand hover:text-white transition"
                >
                  {option.text}
                </button>

              ))}

            </div>

          </>
        )}

        {/* =========================
           RESULT SCREEN
        ========================= */}

        {result && (

          <div className="text-center">

            {/* Result Card */}
            <div
              ref={resultRef}
              className="bg-gradient-to-br from-brand to-[#1A3A6F] text-white p-10 rounded-xl shadow-xl"
            >

              {/* Logo */}
              <img
                src={logo}
                alt="OGTH Logo"
                className="w-16 mx-auto mb-4 rounded-full"
              />

              {/* Result Title */}
              <h2 className="uppercase text-sm tracking-widest opacity-80">
                Your Talent Type
              </h2>

              {/* Result */}
              <h1 className="text-4xl font-bold mt-3">
                {result}
              </h1>

              {/* Result Description */}
              <p className="mt-4 opacity-90">
                {resultDescriptions[result]}
              </p>

              <p className="text-sm mt-6 opacity-70">
                OGTH Talent Discovery Quiz
              </p>

            </div>

            {/* =========================
               ACTION BUTTONS
            ========================= */}

            <div className="mt-6 space-y-3">

              {/* Download Result */}
              <button
                onClick={downloadCard}
                className="w-full bg-brand text-white p-3 rounded-md"
              >
                Download Result Card
              </button>

              {/* Retake Quiz */}
              <button
                onClick={retakeQuiz}
                className="w-full bg-gray-700 text-white p-3 rounded-md"
              >
                Retake Quiz
              </button>

              {/* Share on X */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  shareText
                )}`}
                target="_blank"
                className="block bg-black text-white p-3 rounded-md"
              >
                Share on X
              </a>

              {/* Share on WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                className="block bg-green-500 text-white p-3 rounded-md"
              >
                Share on WhatsApp
              </a>

              {/* Share on LinkedIn */}
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${quizUrl}`}
                target="_blank"
                className="block bg-blue-700 text-white p-3 rounded-md"
              >
                Share on LinkedIn
              </a>

            </div>

          </div>

        )}

      </div>

    </section>
  );
}